import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
    Box,
    Button,
    Collapse,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';

import { useDeviceAlertConfiguration } from '@/api/customSensorAlerts';
import { SensorAlertTypes } from '@/api/customSensorAlerts/types';
import { thresholdIcon } from '@/assets';
import { SensorIcon } from '@/components/data/SensorIcon';
import { AlertReferenceChart } from '@/components/spotter/notificationTab/AlertReferenceChart';
import { SensorAlertEditForm } from '@/components/spotter/notificationTab/SensorAlertEditForm';
import { SensorAlertRow } from '@/components/spotter/notificationTab/SensorAlertRow';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { useSpotterContext } from '@/context/SpotterContext';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { AlertComparisonOperators, sortDeviceAlerts } from '@/utils';

export type AlertEditFormValues = {
    alertType: SensorAlertTypes;
    logicalOperator: AlertComparisonOperators;
    thresholdValue: string;
    sensorPosition: string;
};

export const newAlertDefaultValues: AlertEditFormValues = {
    alertType: '' as SensorAlertTypes,
    logicalOperator: '>',
    thresholdValue: '',
    sensorPosition: '',
};

export const CustomSensorAlertsSection = () => {
    const { spotterId } = useSpotterContext();
    const { isOpen, toggle, close } = useDisclosure();
    const { data: deviceAlerts, isFetching } = useDeviceAlertConfiguration({
        spotterId: spotterId!,
        config: { enabled: !!spotterId },
    });

    const formMethods = useForm<AlertEditFormValues>({
        defaultValues: newAlertDefaultValues,
        mode: 'onChange',
    });
    useUnsavedChangesGuard(formMethods.formState.isDirty, 'sensor-alert-new');

    const [alertTypeFormValue] = formMethods.watch(['alertType']);

    const onFormReset = () => {
        formMethods.reset(newAlertDefaultValues);
    };

    return (
        <Stack gap={2}>
            <SectionHeader isLoading={isFetching} title="Sensor alerts" icon={<SensorIcon src={thresholdIcon} />} />
            {!!deviceAlerts?.data?.length && (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">Type</TableCell>
                                <TableCell align="center">Trigger threshold</TableCell>
                                <TableCell align="center">Sensor position</TableCell>
                                <TableCell></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {deviceAlerts?.data
                                .toSorted(sortDeviceAlerts)
                                .map((row) => <SensorAlertRow key={row.id} row={row} />)}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Button
                fullWidth
                variant="outlined"
                startIcon={isOpen ? <ExpandLessIcon /> : <AddCircleIcon />}
                onClick={toggle}
            >
                Create new alert
            </Button>
            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                <FormProvider {...formMethods}>
                    <Stack
                        direction="row"
                        sx={{
                            flexWrap: { xs: 'wrap-reverse', md: 'nowrap' },
                            gap: 2,
                            justifyContent: 'center',
                            maxWidth: '100%',
                        }}
                    >
                        <Box sx={{ display: 'flex', flexGrow: 1 }}>
                            <AlertReferenceChart enabled={isOpen} alertType={alertTypeFormValue as SensorAlertTypes} />
                        </Box>
                        <SensorAlertEditForm isOpen={isOpen} close={close} onReset={onFormReset} />
                    </Stack>
                </FormProvider>
            </Collapse>
        </Stack>
    );
};
